import { Box, Radio, Spinner, Text } from '@factorialco/deprecated-design-system'

import Select from 'modules/core/components/Inputs/Select'
import TextField from 'modules/core/components/TextField'
import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { optionsForReferenceFields } from 'modules/processes/components/ItemWizard/components/Form/Items/Deadline/DueDateForm'
import { StepProgressionCondtion } from 'modules/processes/components/ProcessSidePanel'
import { DueDatesFields, type Process } from 'modules/processes/hooks/useProcesses'
import { useTemplate } from 'modules/processes/hooks/useTemplate'

import type StepForm from './StepForm'
import { offsetDirection } from './offsetDirection'

interface Props {
  form: StepForm
  process: Process
}

const EXCLUDED_REFERENCE_OPTIONS = [DueDatesFields.StepStartDate.toString()]

const OffsetOptions = ({ form, process }: Props) => {
  const { progression_condition_resource_key, progression_condition } = form.values

  const templateId = process?.template?.id || process?.category
  const { template, loading: loadingTemplate } = useTemplate(String(templateId))
  const referenceOptions = optionsForReferenceFields(
    form.get('progression_condition_resource_key').value,
    template?.orderedVisibleFields || [],
    template
  ).filter((option) => !EXCLUDED_REFERENCE_OPTIONS.includes(option.value))

  const progressionOptions = offsetDirection(form.get('progression_condition_resource_key').value)

  const onChangeReferenceKey = () => {
    // set offset direaction default value when changing progression condtion reference key
    form.get('offset_direction').set(progressionOptions[0]?.value)
  }

  // If the step has not progression_condition_resource_key, we have to assign it
  // a value by default when we display this on condition by date
  useEffect(() => {
    if (
      progression_condition === StepProgressionCondtion.ByDate &&
      progression_condition_resource_key === ''
    ) {
      form.get('progression_condition_resource_key').set(referenceOptions[0]?.value)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progression_condition_resource_key, progression_condition])

  return (
    <>
      <Box gap="s8" paddingY="s12">
        <Text.Normal size="300" weight="semibold">
          {i18n.t('processes.update_process.step_progression')}
        </Text.Normal>
        <Text.Normal>{i18n.t('processes.update_process.step_progression_desc')}</Text.Normal>
      </Box>
      <Radio.Wrap>
        <Box gap="s16">
          <Radio
            name="by_date"
            value={progression_condition}
            onChange={() =>
              form.setValues({ progression_condition: StepProgressionCondtion.ByDate })
            }
            description={i18n.t('processes.update_process.schedule_desc')}
            option={{
              label: i18n.t('processes.update_process.schedule'),
              value: StepProgressionCondtion.ByDate,
            }}
          >
            <Box width="full">
              {loadingTemplate ? (
                <Spinner />
              ) : (
                <Box gap="s12" flexDirection="column">
                  <Text.Normal size="300">
                    {i18n.t('processes.update_process.select_date')}
                  </Text.Normal>
                  <Box flexDirection="row" alignItems="center" gap="s6">
                    <TextField
                      min={1}
                      mandatory
                      type="number"
                      value={form.get('offset_amount').value}
                      placeholder={i18n.t(
                        'processes.process_settings.tasks.form.days_offset_placeholder'
                      )}
                      onChange={(value) => {
                        form.setValues({ offset_amount: +value })
                      }}
                    />
                    <Text.Normal size="300">{i18n.t('processes.update_process.days')}</Text.Normal>
                    <Select field={form.get('offset_direction')} options={progressionOptions} />
                  </Box>
                  <Select
                    field={form.get('progression_condition_resource_key')}
                    value={progression_condition_resource_key}
                    defaultValue={referenceOptions[0]?.value}
                    options={referenceOptions}
                    onChange={onChangeReferenceKey}
                  />
                </Box>
              )}
            </Box>
          </Radio>

          <Radio
            name="automatic"
            value={progression_condition}
            onChange={() =>
              form.setValues({ progression_condition: StepProgressionCondtion.Automatic })
            }
            description={i18n.t('processes.update_process.automatic_description')}
            option={{
              label: i18n.t('processes.update_process.automatic'),
              value: StepProgressionCondtion.Automatic,
            }}
          />

          <Radio
            name="manual"
            value={progression_condition}
            onChange={() =>
              form.setValues({ progression_condition: StepProgressionCondtion.Manual })
            }
            description={i18n.t('processes.update_process.manual_description')}
            option={{
              label: i18n.t('processes.update_process.manual'),
              value: StepProgressionCondtion.Manual,
            }}
          />
        </Box>
      </Radio.Wrap>
    </>
  )
}

export default OffsetOptions
